import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { QuizFormModal } from './QuizFormModal'
import { QuestionFormModal } from './QuestionFormModal'
import { ResetQuizAttemptsModal } from './ResetQuizAttemptsModal'
import {
  useQuizByLesson,
  useQuestions,
  useCreateQuiz,
  useUpdateQuiz,
  useDeleteQuiz,
  usePublishQuiz,
  useAddQuestion,
  useUpdateQuestion,
  useDeleteQuestion,
  useReorderQuestions,
  useQuizAttemptsSummary,
  useResetQuizAttempts,
} from '@/hooks'
import { useAuth } from '@/hooks/useAuth'
import type { Question, CreateQuizRequest } from '@/types/quiz'

interface QuizManagerProps {
  lessonId: string
  lessonTitle: string
}

export const QuizManager: React.FC<QuizManagerProps> = ({ lessonId, lessonTitle }) => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const overviewPath = user?.role === 'admin' || user?.role === 'super_admin' ? '/admin' : '/instructor'
  const { data: quiz, isLoading: quizLoading } = useQuizByLesson(lessonId)
  const { data: questions, isLoading: questionsLoading } = useQuestions(quiz?.id || '')
  const { data: attemptsSummary, isLoading: summaryLoading } = useQuizAttemptsSummary(quiz?.id || '')
  const hasEssay = questions?.some((q) => q.questionType === 'essay') || false

  const createQuiz = useCreateQuiz()
  const updateQuiz = useUpdateQuiz()
  const deleteQuiz = useDeleteQuiz()
  const publishQuiz = usePublishQuiz()
  const addQuestion = useAddQuestion()
  const updateQuestion = useUpdateQuestion()
  const deleteQuestion = useDeleteQuestion()
  const reorderQuestions = useReorderQuestions()
  const resetQuizAttempts = useResetQuizAttempts()

  const [showQuizModal, setShowQuizModal] = useState(false)
  const [showQuestionModal, setShowQuestionModal] = useState(false)
  const [showResetModal, setShowResetModal] = useState(false)
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Safe spread with fallback to empty array
  const sortedQuestions = Array.isArray(questions)
    ? [...questions].sort((a, b) => a.orderIndex - b.orderIndex)
    : []

  const handleCreateQuiz = async (data: CreateQuizRequest) => {
    await createQuiz.mutateAsync(data)
    setShowQuizModal(false)
  }

  const handleUpdateQuiz = async (data: CreateQuizRequest) => {
    if (!quiz) return
    await updateQuiz.mutateAsync({ id: quiz.id, data })
    setShowQuizModal(false)
  }

  const handleDeleteQuiz = async () => {
    if (!quiz) return
    if (!confirm('Are you sure you want to delete this quiz? All questions will be deleted.')) return
    setIsDeleting(true)
    try {
      await deleteQuiz.mutateAsync(quiz.id)
    } finally {
      setIsDeleting(false)
    }
  }

  const handlePublishQuiz = async () => {
    if (!quiz) return
    if (!questions || questions.length === 0) {
      alert('Please add at least one question before publishing.')
      return
    }
    await publishQuiz.mutateAsync(quiz.id)
  }

  const handleResetAttempts = () => {
    setShowResetModal(true)
  }

  const handleConfirmReset = async (enrollmentId: string) => {
    if (!quiz) return
    try {
      await resetQuizAttempts.mutateAsync({ quizId: quiz.id, enrollmentId })
      alert('Quiz attempts reset successfully!')
    } catch (error) {
      console.error('Failed to reset quiz attempts:', error)
      alert('Failed to reset quiz attempts. Please try again.')
    }
  }

  const handleAddQuestion = async (data: Omit<Question, 'id' | 'quizId'>) => {
    if (!quiz) return
    await addQuestion.mutateAsync({
      quizId: quiz.id,
      question: { ...data, orderIndex: questions?.length || 0 },
    })
    setShowQuestionModal(false)
  }

  const handleUpdateQuestion = async (data: Omit<Question, 'id' | 'quizId'>) => {
    if (!quiz || !editingQuestion) return
    await updateQuestion.mutateAsync({
      quizId: quiz.id,
      questionId: editingQuestion.id,
      question: data,
    })
    setEditingQuestion(null)
    setShowQuestionModal(false)
  }

  const handleDeleteQuestion = async (questionId: string) => {
    if (!quiz) return
    if (!confirm('Are you sure you want to delete this question?')) return
    await deleteQuestion.mutateAsync({ quizId: quiz.id, questionId })
  }

  const handleMoveQuestion = async (questionId: string, direction: 'up' | 'down') => {
    if (!quiz) return
    const currentIndex = sortedQuestions.findIndex((q) => q.id === questionId)
    if (currentIndex === -1) return
    if (direction === 'up' && currentIndex === 0) return
    if (direction === 'down' && currentIndex === sortedQuestions.length - 1) return

    const newQuestions = [...sortedQuestions]
    const swapIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1
    ;[newQuestions[currentIndex], newQuestions[swapIndex]] = [
      newQuestions[swapIndex],
      newQuestions[currentIndex],
    ]

    const questionIds = newQuestions.map((q) => q.id)
    await reorderQuestions.mutateAsync({ quizId: quiz.id, questionIds })
  }

  const openEditQuestion = (question: Question) => {
    setEditingQuestion(question)
    setShowQuestionModal(true)
  }

  const openAddQuestion = () => {
    setEditingQuestion(null)
    setShowQuestionModal(true)
  }

  if (quizLoading) {
    return (
      <div className="p-6 rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/10">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-100 dark:bg-white/10 rounded w-1/3 mb-4" />
          <div className="h-4 bg-gray-100 dark:bg-white/10 rounded w-2/3" />
        </div>
      </div>
    )
  }

  // No quiz exists yet
  if (!quiz) {
    return (
      <div className="p-6 rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/10">
        <div className="text-center py-8">
          <div className="size-16 rounded-full bg-gray-100 dark:bg-white/5 mx-auto mb-4 flex items-center justify-center">
            <span className="material-symbols-outlined text-[32px] text-gray-500">quiz</span>
          </div>
          <h4 className="text-gray-900 dark:text-white font-bold mb-2">No Quiz Created</h4>
          <p className="text-sm text-gray-500 dark:text-text-muted mb-4">
            Buat kuis untuk menguji peserta pada "{lessonTitle}"
          </p>
          <button
            onClick={() => setShowQuizModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg font-bold hover:bg-primary/90 transition-colors"
          >
            <span className="material-symbols-outlined text-lg">add</span>
            Create Quiz
          </button>
        </div>

        {showQuizModal && (
          <QuizFormModal
            lessonId={lessonId}
            onSave={handleCreateQuiz}
            onClose={() => setShowQuizModal(false)}
            isSaving={createQuiz.isPending}
          />
        )}
      </div>
    )
  }

  // Quiz exists
  return (
    <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/10 overflow-hidden">
      {/* Quiz Header */}
      <div className="p-5 border-b border-gray-200 dark:border-white/10">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="text-lg font-bold text-gray-900 dark:text-white">{quiz.title}</h4>
              <span
                className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                  quiz.isPublished
                    ? 'bg-green-500/20 text-green-400'
                    : 'bg-yellow-500/20 text-yellow-400'
                }`}
              >
                {quiz.isPublished ? 'Published' : 'Draft'}
              </span>
            </div>
            {quiz.description && (
              <p className="text-sm text-gray-500 dark:text-text-muted mb-3">{quiz.description}</p>
            )}
            <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 dark:text-text-muted">
              <span className="flex items-center gap-1">
                <span className="material-symbols-outlined text-sm">percent</span>
                Pass: {quiz.passingScore}%
              </span>
              <span className="flex items-center gap-1">
                <span className="material-symbols-outlined text-sm">timer</span>
                {quiz.timeLimitMinutes} min
              </span>
              <span className="flex items-center gap-1">
                <span className="material-symbols-outlined text-sm">replay</span>
                {quiz.maxAttempts} attempts
              </span>
              <span className="flex items-center gap-1">
                <span className="material-symbols-outlined text-sm">help</span>
                {questions?.length || 0} questions
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!quiz.isPublished && (
              <button
                onClick={handlePublishQuiz}
                disabled={(questions?.length || 0) === 0 || publishQuiz.isPending}
                className="px-3 py-2 rounded-lg bg-green-500/20 text-green-400 text-sm font-medium hover:bg-green-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                title={(questions?.length || 0) === 0 ? 'Add at least one question to publish' : 'Publish quiz'}
              >
                <span className="material-symbols-outlined text-base">publish</span>
                Publish
              </button>
            )}
            {quiz.isPublished && (
              <button
                onClick={handleResetAttempts}
                disabled={resetQuizAttempts.isPending}
                className="px-3 py-2 rounded-lg bg-orange-500/20 text-orange-400 text-sm font-medium hover:bg-orange-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                title="Reset percobaan kuis peserta (Admin only)"
              >
                <span className="material-symbols-outlined text-base">restart_alt</span>
                Reset Attempts
              </button>
            )}
            {quiz.isPublished && hasEssay && (
              <button
                onClick={() => navigate(`${overviewPath}/quizzes/${quiz.id}`)}
                className="px-3 py-2 rounded-lg bg-blue-500/20 text-blue-400 text-sm font-medium hover:bg-blue-500/30 transition-colors flex items-center gap-1.5"
                title="Review essay answers"
              >
                <span className="material-symbols-outlined text-base">rate_review</span>
                Review Essays
              </button>
            )}
            <button
              onClick={() => setShowQuizModal(true)}
              className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
              title="Edit quiz settings"
            >
              <span className="material-symbols-outlined text-gray-500 dark:text-gray-400">edit</span>
            </button>
            <button
              onClick={handleDeleteQuiz}
              disabled={isDeleting}
              className="p-2 rounded-lg hover:bg-red-500/20 transition-colors"
              title="Delete quiz"
            >
              <span className="material-symbols-outlined text-red-400">delete</span>
            </button>
          </div>
        </div>
      </div>

      {/* Questions List */}
      <div className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h5 className="text-sm font-bold text-gray-900 dark:text-white">Questions</h5>
          <button
            onClick={openAddQuestion}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-white/5 text-sm text-primary hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
          >
            <span className="material-symbols-outlined text-base">add</span>
            Add Question
          </button>
        </div>

        {questionsLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse p-4 rounded-lg bg-gray-100 dark:bg-white/5">
                <div className="h-4 bg-gray-100 dark:bg-white/10 rounded w-3/4" />
              </div>
            ))}
          </div>
        ) : sortedQuestions.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-text-muted">
            <p className="text-sm">No questions added yet.</p>
            <p className="text-xs mt-1">Add questions to complete this quiz.</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[240px] overflow-y-auto custom-scrollbar pr-1">
            {sortedQuestions.map((question, index) => (
              <div
                key={question.id}
                className="group p-4 rounded-lg bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/5 hover:border-gray-200 dark:border-white/10 transition-colors"
              >
                <div className="flex items-start gap-3">
                  {/* Order number */}
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                    <span className="text-sm font-bold text-primary">{index + 1}</span>
                  </div>

                  {/* Question content */}
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-900 dark:text-white text-sm line-clamp-2">{question.questionText}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 dark:text-text-muted">
                      <span className="flex items-center gap-1">
                        <span className="material-symbols-outlined text-sm">
                          {question.questionType === 'multiple_choice'
                            ? 'radio_button_checked'
                            : 'edit_note'}
                        </span>
                        {question.questionType === 'multiple_choice' ? 'Multiple Choice' : 'Essay'}
                      </span>
                      <span>{question.points} pts</span>
                      {question.questionType === 'multiple_choice' && (
                        <span>{question.options.length} options</span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex-shrink-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => handleMoveQuestion(question.id, 'up')}
                      disabled={index === 0 || reorderQuestions.isPending}
                      className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move up"
                    >
                      <span className="material-symbols-outlined text-gray-500 dark:text-gray-400 text-lg">
                        arrow_upward
                      </span>
                    </button>
                    <button
                      onClick={() => handleMoveQuestion(question.id, 'down')}
                      disabled={
                        index === sortedQuestions.length - 1 || reorderQuestions.isPending
                      }
                      className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move down"
                    >
                      <span className="material-symbols-outlined text-gray-500 dark:text-gray-400 text-lg">
                        arrow_downward
                      </span>
                    </button>
                    <button
                      onClick={() => openEditQuestion(question)}
                      className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
                      title="Edit question"
                    >
                      <span className="material-symbols-outlined text-gray-500 dark:text-gray-400 text-lg">edit</span>
                    </button>
                    <button
                      onClick={() => handleDeleteQuestion(question.id)}
                      disabled={deleteQuestion.isPending}
                      className="p-1.5 rounded-lg hover:bg-red-500/20 transition-colors"
                      title="Delete question"
                    >
                      <span className="material-symbols-outlined text-red-400 text-lg">delete</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {showQuizModal && (
        <QuizFormModal
          quiz={quiz}
          lessonId={lessonId}
          onSave={quiz ? handleUpdateQuiz : handleCreateQuiz}
          onClose={() => setShowQuizModal(false)}
          isSaving={quiz ? updateQuiz.isPending : createQuiz.isPending}
        />
      )}

      {showQuestionModal && (
        <QuestionFormModal
          question={editingQuestion}
          quizId={quiz.id}
          onSave={editingQuestion ? handleUpdateQuestion : handleAddQuestion}
          onClose={() => {
            setShowQuestionModal(false)
            setEditingQuestion(null)
          }}
          isSaving={editingQuestion ? updateQuestion.isPending : addQuestion.isPending}
        />
      )}

      {showResetModal && quiz && (
        <ResetQuizAttemptsModal
          quizId={quiz.id}
          summaries={attemptsSummary || []}
          isLoading={summaryLoading}
          onConfirmReset={handleConfirmReset}
          onClose={() => setShowResetModal(false)}
        />
      )}
    </div>
  )
}

export default QuizManager
